import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { useKeyboard, useOfferFilterParams } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import { readListParams, updateListParams, writeFilterValues } from 'components/ArticlesTable/offerFilterSchema';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_INPUT_NAME = 'offerName';

/**
 * Liste durumunun tek kaynağı tarayıcı URL'si: filtreler, arama metni, sayfa, sayfa boyutu ve sıralama URL'den okunur,
 * her kullanıcı işlemi URL'ye yeni bir geçmiş kaydı olarak yazılır. Geri/ileri ve yenileme aynı durumu geri getirir.
 */
export const useOfferListState = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { fields, values, apply } = useOfferFilterParams();
  const { title, page, pageSize, order } = useMemo(() => readListParams(search), [search]);

  // Arama kutusu her tuşta `onSearch` çağırır; kutu anında güncellenir, URL'ye yazım gecikmeli yapılır ki her harf
  // ayrı bir geçmiş kaydı olmasın. URL değişince (geri/ileri) kutu URL'deki metne döner.
  const [searchText, setSearchText] = useState(title);
  const { handleDebounce: debounceSearch, cancelDebounce: cancelSearch } = useDebounce();
  const latestSearch = useRef();

  useEffect(() => {
    setSearchText(title);
  }, [title]);

  // Arama kutusu ekran klavyesinin global değerini açılışta kendine uygular; listeye dönüşte önceki ziyaretten kalan
  // metin URL'deki aramayı ezmesin diye bekleyen yazım iptal edilir ve klavye değeri URL'deki metne eşitlenir.
  // Kutunun effect'i bu effect'ten önce çalışır.
  const { inputName: keyboardInputName, setValue: setKeyboardValue } = useKeyboard();
  useEffect(() => {
    cancelSearch();
    setSearchText(title);
    if (keyboardInputName === SEARCH_INPUT_NAME) {
      setKeyboardValue(title);
    }
    // Yalnız açılışta çalışır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eski biçimli bir link tek biçime çevrilir; filtre kümesi değişmediği için `replace` ile, geçmişe kayıt eklenmeden.
  useEffect(() => {
    apply(values);
  }, [apply, values]);

  const changeList = useCallback(
    (changes) => {
      const nextSearch = updateListParams(search, changes);
      if (nextSearch !== updateListParams(search, {})) {
        navigate({ search: nextSearch });
      }
    },
    [navigate, search]
  );

  latestSearch.current = (nextTitle) => {
    // Kutu odaklanınca da mevcut metinle çağrılır; metin değişmediyse sayfa korunur.
    if (nextTitle !== title) {
      changeList({ title: nextTitle, page: 1 });
    }
  };

  const onSearch = (text) => {
    setSearchText(text);
    debounceSearch({ delay: SEARCH_DEBOUNCE_MS, callback: () => latestSearch.current(text.trim()) });
  };

  // Sütun, kendi verisini süzen bir alan uygulanmışsa vurgulanır.
  const filteredColumns = {
    title: Boolean(title),
    author: 'author' in values,
    createdAt: 'createdAt' in values,
    ownerCommittee: 'ownerCommittee' in values,
    status: 'status' in values,
  };

  const filtered = useMemo(() => {
    const query = [title && `title=${encodeURIComponent(title)}`, writeFilterValues(values, fields)].filter(Boolean).join('&');
    return query ? `&${query}` : '';
  }, [fields, title, values]);

  return {
    page,
    pageSize,
    order,
    setOrder: (nextOrder) => changeList({ order: nextOrder, page: 1 }),
    filtered,
    isColumnFiltered: (column) => Boolean(filteredColumns[column]),
    searchInputProps: { name: SEARCH_INPUT_NAME, value: searchText, onSearch },
    pagination: {
      current: page,
      pageSize,
      onChange: (nextPage, nextPageSize) => changeList(nextPageSize === pageSize ? { page: nextPage } : { page: 1, pageSize: nextPageSize }),
    },
    appliedFilterCount: Object.keys(values).length,
  };
};
